import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2,
  Play, Flag, Clock, ArrowUpRight,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, Slideout } from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { SeverityChip } from '../widgets/SeverityChip';
import { StatusBadge } from '../widgets/StatusBadge';
import {
  ccListRemediationTasks, ccUpdateRemediationTask, ccResolveRemediation,
  ccVerifyRemediation,
} from '../../../../services/api';

/**
 * RemediationSlideout — V2 Remediation workspace (Phase B2 / G315).
 *
 * Replaces ?legacy=1&tab=REMEDIATION. Production rebuild of V1's
 * remediation tab with the full workflow:
 *
 *   - Severity filter (CRITICAL / HIGH / MEDIUM / LOW)
 *   - Status filter (OPEN / IN_PROGRESS / RESOLVED / VERIFIED / ESCALATED)
 *   - Expandable rows with:
 *       finding summary, control + framework context
 *       status workflow buttons (Start work → Resolve → Verify)
 *       resolution type picker (MITIGATED / CLOSED / ACCEPTED_RISK)
 *       escalation form (reason + escalate)
 *       SLA breach indicator
 *
 * Mounts as a 760px Slideout from the V2 Compliance dashboard.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
const SEVERITY_OPTIONS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const STATUS_OPTIONS = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'VERIFIED', 'ESCALATED'];

export function RemediationSlideout({ isOpen, onClose, addToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState(new Set(['OPEN', 'IN_PROGRESS']));
  const [expanded, setExpanded] = useState(() => new Set());
  const pg = usePaginationState(10);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ccListRemediationTasks();
      const list = Array.isArray(r) ? r : (r?.data || r?.items || r?.tasks || []);
      setItems(Array.isArray(list) ? list : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isOpen) reload(); /* eslint-disable-next-line */ }, [isOpen]);

  const filtered = useMemo(() => items.filter((t) => {
    const sev = String(t.severity || '').toUpperCase();
    const st = String(t.status || '').toUpperCase();
    if (severityFilter.size > 0 && !severityFilter.has(sev)) return false;
    if (statusFilter.size > 0 && !statusFilter.has(st)) return false;
    return true;
  }), [items, severityFilter, statusFilter]);
  const visible = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); /* eslint-disable-next-line */ }, [filtered.length]);

  const toggleFilter = (setter, value) => setter((s) => {
    const n = new Set(s);
    if (n.has(value)) n.delete(value); else n.add(value);
    return n;
  });

  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  /* Stats — what the operator sees first thing in the header */
  const stats = useMemo(() => {
    const open = items.filter((t) => ['OPEN', 'IN_PROGRESS'].includes(String(t.status || '').toUpperCase())).length;
    const critical = items.filter((t) => String(t.severity || '').toUpperCase() === 'CRITICAL'
                                      && ['OPEN', 'IN_PROGRESS'].includes(String(t.status || '').toUpperCase())).length;
    const escalated = items.filter((t) => String(t.status || '').toUpperCase() === 'ESCALATED').length;
    return { open, critical, escalated };
  }, [items]);

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={760}
      ariaLabel="Remediation tasks workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-v2-warning" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Remediation tasks</h2>
            <Pill tone="warning" size="sm">{stats.open} open</Pill>
            {stats.critical > 0 && <Pill tone="danger" size="sm" dot>{stats.critical} critical</Pill>}
            {stats.escalated > 0 && <Pill tone="danger" size="sm" dot>{stats.escalated} escalated</Pill>}
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0 mb-3">
            Findings discovered by compliance scans + manual reports. Work them through
            Start → Resolve → Verify.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <FilterChips
              label="Severity"
              options={SEVERITY_OPTIONS}
              selected={severityFilter}
              onToggle={(v) => toggleFilter(setSeverityFilter, v)}
              tone="severity"
            />
            <FilterChips
              label="Status"
              options={STATUS_OPTIONS}
              selected={statusFilter}
              onToggle={(v) => toggleFilter(setStatusFilter, v)}
              tone="status"
            />
            <Button variant="ghost" size="sm"
                    className="ml-auto"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && items.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading remediation tasks…</p>
      )}
      {!loading && filtered.length === 0 && (
        <EmptyState
          icon={<CheckCircle2 size={20} />}
          title="No findings match"
          description={items.length === 0
            ? 'No remediation tasks open. Compliance posture is clean.'
            : 'Adjust the severity or status filters to see more findings.'}
        />
      )}
      {filtered.length > 0 && (
        <ul className="space-y-2 m-0 p-0 list-none">
          {visible.map((t) => (
            <RemediationRow
              key={t.id}
              task={t}
              expanded={expanded.has(t.id)}
              onToggle={() => toggleExpand(t.id)}
              onStartWork={async () => {
                const r = await ccUpdateRemediationTask(t.id, { status: 'IN_PROGRESS' });
                if (r?.ok === false) throw new Error(r.error || 'Failed');
                addToast?.(`Started work on ${t.title || t.controlId}.`, 'success');
                reload();
              }}
              onResolve={async (resolutionType, notes) => {
                const r = await ccResolveRemediation(t.id, { resolutionType, notes });
                if (r?.ok === false) throw new Error(r.error || 'Failed');
                addToast?.(`Resolved (${resolutionType}).`, 'success');
                reload();
              }}
              onVerify={async () => {
                const r = await ccVerifyRemediation(t.id);
                if (r?.ok === false) throw new Error(r.error || 'Failed');
                addToast?.('Verified.', 'success');
                reload();
              }}
              onEscalate={async (reason) => {
                const r = await ccUpdateRemediationTask(t.id, { status: 'ESCALATED', escalationReason: reason });
                if (r?.ok === false) throw new Error(r.error || 'Failed');
                addToast?.('Escalated.', 'warn');
                reload();
              }}
              addToast={addToast}
            />
          ))}
        </ul>
      )}

      {filtered.length > 0 && (
        <div className="mt-3">
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="findings"
          />
        </div>
      )}
    </Slideout>
  );
}

/* ───── RemediationRow — expandable card with status workflow ───── */
function RemediationRow({ task: t, expanded, onToggle, onStartWork, onResolve, onVerify, onEscalate, addToast }) {
  const [busy, setBusy] = useState(false);
  const [actionMode, setActionMode] = useState(null);   // null | 'resolve' | 'escalate'
  const [resolutionType, setResolutionType] = useState('MITIGATED');
  const [notes, setNotes] = useState('');

  const status = String(t.status || 'OPEN').toUpperCase();
  const slaDeadline = t.slaDeadline || t.deadline;
  const slaBreached = slaDeadline && new Date(slaDeadline).getTime() < Date.now()
                   && !['RESOLVED', 'VERIFIED'].includes(status);

  const handle = async (fn) => {
    setBusy(true);
    try { await fn(); setActionMode(null); setNotes(''); } catch (e) {
      addToast?.(`Action failed: ${e?.message || e}`, 'error');
    } finally { setBusy(false); }
  };

  return (
    <li className={`bg-v2-surface border rounded-v2-md overflow-hidden ${
      slaBreached ? 'border-v2-danger border-l-4 border-l-v2-danger' : 'border-v2-border-subtle'
    }`}>
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-v2-muted flex items-center gap-3"
      >
        {expanded ? <ChevronDown size={14} className="text-v2-ink-muted shrink-0" />
                  : <ChevronRight size={14} className="text-v2-ink-muted shrink-0" />}
        <SeverityChip severity={t.severity} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-v2-ink text-[13px] truncate">
            {t.title || t.findingTitle || `Finding ${String(t.id).slice(0, 8)}`}
          </div>
          <div className="text-[11px] text-v2-ink-muted truncate flex items-center gap-2">
            {t.controlId && <span className="font-mono-v2">{t.controlId}</span>}
            {t.framework && <><span>·</span><span>{t.framework}</span></>}
            {t.assignee && <><span>·</span><span>{t.assignee}</span></>}
            {slaBreached && (
              <><span>·</span><span className="text-v2-danger font-semibold inline-flex items-center gap-0.5">
                <Clock size={10} /> SLA breached
              </span></>
            )}
          </div>
        </div>
        <StatusBadge status={status} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-v2-border-subtle bg-v2-muted/30 space-y-3">
          {t.findingSummary && (
            <p className="text-[12.5px] text-v2-ink mt-3 m-0">{t.findingSummary}</p>
          )}
          {t.description && !t.findingSummary && (
            <p className="text-[12.5px] text-v2-ink mt-3 m-0">{t.description}</p>
          )}

          {t.evidenceUrl && (
            <a href={t.evidenceUrl} target="_blank" rel="noopener noreferrer"
               className="text-[12px] text-v2-action hover:underline inline-flex items-center gap-1">
              View finding evidence <ArrowUpRight size={11} />
            </a>
          )}

          {/* Status workflow buttons */}
          {actionMode === null && (
            <div className="flex items-center gap-2 pt-2 border-t border-v2-border-subtle flex-wrap">
              {status === 'OPEN' && (
                <Button variant="primary" size="sm" iconLeft={<Play size={13} />}
                        disabled={busy} onClick={() => handle(onStartWork)}>
                  Start work
                </Button>
              )}
              {(status === 'OPEN' || status === 'IN_PROGRESS') && (
                <Button variant="secondary" size="sm" iconLeft={<CheckCircle2 size={13} />}
                        disabled={busy} onClick={() => setActionMode('resolve')}>
                  Resolve
                </Button>
              )}
              {status === 'RESOLVED' && (
                <Button variant="primary" size="sm" iconLeft={<CheckCircle2 size={13} />}
                        disabled={busy} onClick={() => handle(onVerify)}>
                  Verify
                </Button>
              )}
              {!['RESOLVED', 'VERIFIED', 'ESCALATED'].includes(status) && (
                <Button variant="danger" size="sm" iconLeft={<Flag size={13} />}
                        disabled={busy} onClick={() => setActionMode('escalate')}>
                  Escalate
                </Button>
              )}
              {t.evidenceUrl && (
                <span className="ml-auto text-[10.5px] text-v2-ink-muted">
                  ID {String(t.id).slice(0, 8)}…
                </span>
              )}
            </div>
          )}

          {/* Resolution form */}
          {actionMode === 'resolve' && (
            <div className="space-y-2 pt-2 border-t border-v2-border-subtle">
              <label className="block">
                <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Resolution type</span>
                <select value={resolutionType} onChange={(e) => setResolutionType(e.target.value)}
                        className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px]">
                  <option value="MITIGATED">Mitigated — controls in place to prevent recurrence</option>
                  <option value="CLOSED">Closed — finding addressed</option>
                  <option value="ACCEPTED_RISK">Accepted risk — formal acceptance documented</option>
                </select>
              </label>
              <textarea
                placeholder="Resolution notes (audit-logged)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5 text-[12.5px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
              />
              <div className="flex items-center gap-2">
                <Button variant="primary" size="sm" disabled={busy}
                        onClick={() => handle(() => onResolve(resolutionType, notes))}>
                  {busy ? 'Resolving…' : 'Confirm resolution'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setActionMode(null)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Escalation form */}
          {actionMode === 'escalate' && (
            <div className="space-y-2 pt-2 border-t border-v2-border-subtle">
              <textarea
                placeholder="Escalation reason — required. Goes to compliance lead + audit log."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5 text-[12.5px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
              />
              <div className="flex items-center gap-2">
                <Button variant="danger" size="sm" disabled={busy || !notes.trim()}
                        onClick={() => handle(() => onEscalate(notes))}>
                  {busy ? 'Escalating…' : 'Confirm escalation'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setActionMode(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function FilterChips({ label, options, selected, onToggle, tone }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">{label}:</span>
      {options.map((opt) => {
        const isOn = selected.has(opt);
        return (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            className={`text-[11px] px-2 py-0.5 rounded-v2-pill border transition-colors ${
              isOn
                ? 'bg-v2-action-soft text-v2-action border-v2-action/40'
                : 'bg-v2-surface text-v2-ink-muted border-v2-border-subtle hover:border-v2-action/30'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

export default RemediationSlideout;
